import { createAction } from '@reduxjs/toolkit';
import {
	ChatRef,
	CreditWallet,
	DesignSystem,
	DesignSystemVersion,
	DsChat,
	Frame,
	Organization,
	OrganizationCreditWallet,
	OrganizationRole,
	Scene,
	SceneChat,
	SceneComment,
	SceneDsLink,
	DsMode,
	Subscription,
	User,
	DsVersionRef,
} from 'dragonmouse-shared/interfaces';
import { OptimisticWrite } from './reducers';

export type OrganizationWithRole = Organization & { role: OrganizationRole };

// Data flow: server → polling streams → DB slice → selectors → components.
// The update* actions below are for polling streams only — they set raw server state.
// To mutate data from components, use forge.ts methods which handle optimistic writes.
// Never dispatch update* actions directly from component code.

//------------------------------------------------------------------------------------------------
// scenes
//------------------------------------------------------------------------------------------------
export const updateScenes = createAction<Scene[]>('GET_SCENES');
export const optimisticallyUpdateScenes = createAction<OptimisticWrite<Scene>[]>(
	'OPTIMISTICALLY_UPDATE_SCENES'
);
export const resolveOptimisticallyUpdatedScenes = createAction<string[]>(
	'RESOLVE_OPTIMISTICALLY_UPDATED_SCENES'
);
export const setSceneLoadError = createAction<string | null>('SET_SCENE_LOAD_ERROR');
// guest
export const updateGuestScenes = createAction<Scene[]>('UPDATE_GUEST_SCENES');

//------------------------------------------------------------------------------------------------
// design systems
//------------------------------------------------------------------------------------------------
export const updateDesignSystems = createAction<DesignSystem[]>('UPDATE_DESIGN_SYSTEMS');
export const optimisticallyUpdateDesignSystems = createAction<OptimisticWrite<DesignSystem>[]>(
	'OPTIMISTICALLY_UPDATE_DESIGN_SYSTEMS'
);
export const resolveOptimisticallyUpdatedDesignSystems = createAction<string[]>(
	'RESOLVE_OPTIMISTICALLY_UPDATED_DESIGN_SYSTEMS'
);
export const setDesignSystemLoadError = createAction<string | null>('SET_DESIGN_SYSTEM_LOAD_ERROR');
// guest
export const updateGuestDesignSystems = createAction<DesignSystem[]>('UPDATE_GUEST_DESIGN_SYSTEMS');
export const updateActiveDesignSystemVersion = createAction<DesignSystemVersion | null>(
	'UPDATE_ACTIVE_DESIGN_SYSTEM_VERSION'
);
export const updateDsVersionRefs = createAction<DsVersionRef[]>('UPDATE_DS_VERSION_REFS');
export const setDsPreparing = createAction<boolean>('SET_DS_PREPARING');
//------------------------------------------------------------------------------------------------
// ds chat
//------------------------------------------------------------------------------------------------
export const updateDsChatRefs = createAction<ChatRef[]>('UPDATE_DS_CHAT_REFS');
export const updateActiveDsChat = createAction<DsChat | null>('UPDATE_ACTIVE_DS_CHAT');
export const optimisticallyUpdateDsChats = createAction<OptimisticWrite<DsChat>[]>(
	'OPTIMISTICALLY_UPDATE_DS_CHATS'
);
export const resolveOptimisticallyUpdatedDsChats = createAction<string[]>(
	'RESOLVE_OPTIMISTICALLY_UPDATED_DS_CHATS'
);

//------------------------------------------------------------------------------------------------
// frames
//------------------------------------------------------------------------------------------------
export const updateFrames = createAction<Frame[]>('UPDATE_FRAMES');
export const optimisticallyUpdateFrames = createAction<OptimisticWrite<Frame>[]>(
	'OPTIMISTICALLY_UPDATE_FRAMES'
);
export const resolveOptimisticallyUpdatedFrames = createAction<string[]>(
	'RESOLVE_OPTIMISTICALLY_UPDATED_FRAMES'
);
// guest
export const updateGuestFrames = createAction<Frame[]>('UPDATE_GUEST_FRAMES');

//------------------------------------------------------------------------------------------------
// scene chats
//------------------------------------------------------------------------------------------------
export const updateActiveSceneChat = createAction<SceneChat | null>('UPDATE_ACTIVE_SCENE_CHAT');
export const optimisticallyUpdateSceneChats = createAction<OptimisticWrite<SceneChat>[]>(
	'OPTIMISTICALLY_UPDATE_SCENE_CHATS'
);
export const resolveOptimisticallyUpdatedSceneChats = createAction<string[]>(
	'RESOLVE_OPTIMISTICALLY_UPDATED_SCENE_CHATS'
);
export const updateSceneChatRefs = createAction<ChatRef[]>('UPDATE_SCENE_CHAT_REFS');
export const updateSceneComments = createAction<SceneComment[]>('UPDATE_SCENE_COMMENTS');
export const optimisticallyUpdateSceneComments = createAction<OptimisticWrite<SceneComment>[]>(
	'OPTIMISTICALLY_UPDATE_SCENE_COMMENTS'
);
export const resolveOptimisticallyUpdatedSceneComments = createAction<string[]>(
	'RESOLVE_OPTIMISTICALLY_UPDATED_SCENE_COMMENTS'
);
export const clearActiveScene = createAction('CLEAR_ACTIVE_SCENE');
export const updateActiveSceneDsLink = createAction<SceneDsLink | null>(
	'UPDATE_ACTIVE_SCENE_DS_LINK'
);
export const updateActiveSceneDesignSystemModes = createAction<DsMode[] | null>(
	'UPDATE_ACTIVE_SCENE_DESIGN_SYSTEM_MODES'
);

//------------------------------------------------------------------------------------------------
// user
//------------------------------------------------------------------------------------------------
export const updateUser = createAction<User | null>('UPDATE_USER');
export const optimisticallyUpdateUser = createAction<OptimisticWrite<User>[]>(
	'OPTIMISTICALLY_UPDATE_USER'
);
export const resolveOptimisticallyUpdatedUser = createAction<string[]>(
	'RESOLVE_OPTIMISTICALLY_UPDATED_USER'
);

//------------------------------------------------------------------------------------------------
// subscription
//------------------------------------------------------------------------------------------------
export const updateSubscription = createAction<Subscription | null>('UPDATE_SUBSCRIPTION');

//------------------------------------------------------------------------------------------------
// credit wallet
//------------------------------------------------------------------------------------------------
export const updateCreditWallet = createAction<CreditWallet | null>('UPDATE_CREDIT_WALLET');

//------------------------------------------------------------------------------------------------
// tracked users
//------------------------------------------------------------------------------------------------
export const updateTrackedUsers = createAction<User[]>('UPDATE_TRACKED_USERS');

//------------------------------------------------------------------------------------------------
// organizations
//------------------------------------------------------------------------------------------------
export const updateOrganizations = createAction<OrganizationWithRole[]>('UPDATE_ORGANIZATIONS');
export const updateOrganizationSceneIds = createAction<Record<string, string[]>>(
	'UPDATE_ORGANIZATION_SCENE_IDS'
);
export const updateOrganizationDesignSystemIds = createAction<Record<string, string[]>>(
	'UPDATE_ORGANIZATION_DESIGN_SYSTEM_IDS'
);
export const updateOrganizationCreditWallets = createAction<
	Record<string, OrganizationCreditWallet>
>('UPDATE_ORGANIZATION_CREDIT_WALLETS');
export const updateUserSceneIds = createAction<string[]>('UPDATE_USER_SCENE_IDS');
export const updateUserDesignSystemIds = createAction<string[]>('UPDATE_USER_DESIGN_SYSTEM_IDS');

//------------------------------------------------------------------------------------------------
// loading state (tracks things that are loaded by streams)
//------------------------------------------------------------------------------------------------
export type LoadingStateType = 'never-loaded' | 'loading' | 'loaded';

export interface LoadingState {
	profile: LoadingStateType;
	scenes: LoadingStateType;
	activeDesignSystemVersion: LoadingStateType;
	activeDsChat: LoadingStateType;
	activeScene: {
		frames: LoadingStateType;
		chats: LoadingStateType;
	};
	guest: {
		scenes: LoadingStateType;
		frames: LoadingStateType;
		designSystems: LoadingStateType;
	};
}

export const setLoadingState = createAction<{ resource: string; state: LoadingStateType }>(
	'SET_LOADING_STATE'
);
